import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { useAuth, useTheme } from "../App";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";
import {
  Settings,
  Image,
  Video,
  FileText,
  Hash,
  Newspaper,
  Plus,
  Pencil,
  Trash2,
  Save,
  ArrowLeft,
  Moon,
  Sun,
  Eye,
  RefreshCw,
  LayoutDashboard,
  GripVertical,
  ShieldX,
  Users,
  Shield,
  BookPlus,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Calendar,
  MessageSquare,
  Target,
  Sparkles,
  Search,
  Volume2,
  Book,
  Flame,
  Link2,
  Heart,
  User,
  Baby,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format image URLs
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl; // External URL
  }
  if (imageUrl.startsWith("/api/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`; // API path
  }
  return imageUrl; // Already formatted or relative path
};

export default function AdminCMS() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("heroes");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Check admin role
  const isAdmin = user?.role === "admin";

  // Data states
  const [heroes, setHeroes] = useState([]);
  const [videos, setVideos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [articles, setArticles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [settings, setSettings] = useState({});
  const [contributions, setContributions] = useState([]);
  const [contributionFilter, setContributionFilter] = useState("pending");
  const [popularLinks, setPopularLinks] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [prenoms, setPrenoms] = useState([]);
  
  // Trends states
  const [wordOfDay, setWordOfDay] = useState(null);
  const [expressionOfWeek, setExpressionOfWeek] = useState(null);
  const [allWords, setAllWords] = useState([]);
  const [selectedWordId, setSelectedWordId] = useState("");
  const [expressionForm, setExpressionForm] = useState({
    expression: "",
    meaning: "",
    context: "",
    literal: ""
  });
  
  // Words management states
  const [wordsSearch, setWordsSearch] = useState("");
  const [wordsCategory, setWordsCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Fetch all CMS data
  useEffect(() => {
    if (isAdmin) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [heroesRes, videosRes, galleryRes, articlesRes, hashtagsRes, settingsRes, usersRes, contribRes, wodRes, eowRes, wordsRes, categoriesRes, linksRes, postsRes, prenomsRes] = await Promise.all([
        fetch(`${API}/cms/heroes`, { headers }),
        fetch(`${API}/cms/videos`, { headers }),
        fetch(`${API}/cms/gallery`, { headers }),
        fetch(`${API}/cms/articles`, { headers }),
        fetch(`${API}/cms/hashtags`, { headers }),
        fetch(`${API}/cms/settings`, { headers }),
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/contributions?status=pending`, { headers }),
        fetch(`${API}/cms/word-of-day`, { headers }),
        fetch(`${API}/cms/expression-of-week`, { headers }),
        fetch(`${API}/words`, { headers }),
        fetch(`${API}/categories`, { headers }),
        fetch(`${API}/cms/popular-links`, { headers }),
        fetch(`${API}/cms/posts`, { headers }),
        fetch(`${API}/prenoms`, { headers }),
      ]);

      if (heroesRes.ok) setHeroes(await heroesRes.json());
      if (videosRes.ok) setVideos(await videosRes.json());
      if (galleryRes.ok) setGallery(await galleryRes.json());
      if (articlesRes.ok) setArticles(await articlesRes.json());
      if (hashtagsRes.ok) setHashtags(await hashtagsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (usersRes.ok) setAllUsers(await usersRes.json());
      if (contribRes.ok) setContributions(await contribRes.json());
      if (wodRes.ok) {
        const wodData = await wodRes.json();
        setWordOfDay(wodData);
        if (wodData.word) setSelectedWordId(wodData.word.word_id);
      }
      if (eowRes.ok) {
        const eowData = await eowRes.json();
        setExpressionOfWeek(eowData);
        if (eowData.expression) {
          setExpressionForm({
            expression: eowData.expression.expression || "",
            meaning: eowData.expression.meaning || "",
            context: eowData.expression.context || "",
            literal: eowData.expression.literal || ""
          });
        }
      }
      if (wordsRes.ok) setAllWords(await wordsRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (linksRes.ok) setPopularLinks(await linksRes.json());
      if (postsRes.ok) setCommunityPosts(await postsRes.json());
      if (prenomsRes.ok) setPrenoms(await prenomsRes.json());
    } catch (error) {
      console.error("Error fetching CMS data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Open dialog for create/edit
  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditingItem(item);
    setFormData(item || getDefaultFormData(type));
    setDialogOpen(true);
  };

  const getDefaultFormData = (type) => {
    switch (type) {
      case "heroes":
        return {
          title: "",
          subtitle: "",
          excerpt: "",
          category: "ACTUALITÉ",
          category_icon: "📰",
          image_url: "",
          author: "",
          date: new Date().toLocaleDateString("fr-FR"),
          read_time: "5 min",
          is_main: false,
          order: 0,
        };
      case "videos":
        return {
          title: "",
          image_url: "",
          video_url: "",
          duration: "00:00",
          views: "0",
          likes: "0",
        };
      case "gallery":
        return {
          title: "",
          subtitle: "",
          image_url: "",
        };
      case "articles":
        return {
          title: "",
          excerpt: "",
          category: "📚 ÉDUCATION",
          category_icon: "📚",
          image_url: "",
          author: "",
          time: "Il y a 1h",
        };
      case "hashtags":
        return {
          tag: "#",
          posts: 0,
          trend: "new",
        };
      case "words":
        return {
          baoulé: "",
          français: "",
          phonétique: "",
          catégorie: "général",
          exemple_baoulé: "",
          exemple_français: "",
          niveau: "débutant",
        };
      case "popular-links":
        return {
          title: "",
          url: "/",
          order: 0,
        };
      case "posts":
        return {
          author_name: "",
          author_avatar: "",
          content: "",
          image_url: "",
          likes: 0,
          comments: 0,
          time: "À l'instant",
        };
      case "prenoms":
        return {
          prenom: "",
          genre: "masculin",
          signification: "",
          origine: "",
          popularite: 50,
          variantes: [],
        };
      default:
        return {};
    }
  };

  // Save item (create or update)
  const saveItem = async () => {
    setSaving(true);
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    try {
      let url;
      let method = "POST";

      // Handle words separately (different API path)
      if (dialogType === "words") {
        url = `${API}/words`;
        if (editingItem?.word_id) {
          url = `${API}/words/${editingItem.word_id}`;
          method = "PUT";
        }
      } else if (dialogType === "prenoms") {
        url = `${API}/prenoms`;
        if (editingItem?.prenom_id) {
          url = `${API}/prenoms/${editingItem.prenom_id}`;
          method = "PUT";
        }
      } else {
        url = `${API}/cms/${dialogType}`;
        if (editingItem?.id) {
          url += `/${editingItem.id}`;
          method = "PUT";
        }
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingItem ? "Élément mis à jour !" : "Élément créé !");
        setDialogOpen(false);
        fetchAllData();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Delete item
  const deleteItem = async (type, id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;

    const token = localStorage.getItem("token");
    try {
      // Handle words and prenoms separately (different API path)
      let url;
      if (type === "words") {
        url = `${API}/words/${id}`;
      } else if (type === "prenoms") {
        url = `${API}/prenoms/${id}`;
      } else {
        url = `${API}/cms/${type}/${id}`;
      }
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Élément supprimé !");
        fetchAllData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${API}/cms/settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success("Paramètres sauvegardés !");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Settings save error:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Initialize CMS with defaults
  const initializeCMS = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API}/cms/init`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      toast.success(data.message);
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'initialisation");
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success(`Rôle mis à jour: ${newRole}`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Utilisateur supprimé");
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement du CMS...</p>
        </div>
      </div>
    );
  }

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <ShieldX className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Accès Refusé</h1>
          <p className="text-muted-foreground mb-6">
            Cette page est réservée aux administrateurs.
          </p>
          <Button onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-cms-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-muted rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-primary" />
                CMS Admin
              </h1>
              <p className="text-sm text-muted-foreground">
                Gérez le contenu de la page d'accueil
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open("/", "_blank")}
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir le site
            </Button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-muted"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 lg:grid-cols-9 mb-8 h-auto gap-1">
            <TabsTrigger value="heroes" className="flex items-center gap-2">
              <Newspaper className="w-4 h-4" />
              <span className="hidden sm:inline">Héros</span>
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              <span className="hidden sm:inline">Vidéos</span>
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Galerie</span>
            </TabsTrigger>
            <TabsTrigger value="words" className="flex items-center gap-2">
              <BookPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Mots</span>
            </TabsTrigger>
            <TabsTrigger value="prenoms" className="flex items-center gap-2">
              <Baby className="w-4 h-4" />
              <span className="hidden sm:inline">Prénoms</span>
            </TabsTrigger>
            <TabsTrigger value="articles" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Articles</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Tendances</span>
            </TabsTrigger>
            <TabsTrigger value="community" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Communauté</span>
            </TabsTrigger>
            <TabsTrigger value="hashtags" className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              <span className="hidden sm:inline">Hashtags</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Utilisateurs</span>
            </TabsTrigger>
            <TabsTrigger value="contributions" className="flex items-center gap-2 relative">
              <CheckCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Contributions</span>
              {contributions.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {contributions.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Paramètres</span>
            </TabsTrigger>
          </TabsList>

          {/* HEROES TAB */}
          <TabsContent value="heroes">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Articles Héros</h2>
              <Button onClick={() => openDialog("heroes")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <div className="grid gap-4">
              {heroes.map((hero) => (
                <Card key={hero.id} className="overflow-hidden">
                  <div className="flex">
                    {hero.image_url && (
                      <div className="w-40 h-32 flex-shrink-0">
                        <img
                          src={getImageUrl(hero.image_url)}
                          alt={hero.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-primary">
                              {hero.category_icon} {hero.category}
                            </span>
                            {hero.is_main && (
                              <span className="px-2 py-0.5 rounded bg-primary text-white text-xs">
                                PRINCIPAL
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-lg">{hero.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {hero.excerpt}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {hero.author} • {hero.date}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog("heroes", hero)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteItem("heroes", hero.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
              {heroes.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">Aucun article héros</p>
                  <Button onClick={initializeCMS}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Initialiser avec les valeurs par défaut
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* VIDEOS TAB */}
          <TabsContent value="videos">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Vidéos</h2>
              <Button onClick={() => openDialog("videos")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <Card key={video.id} className="overflow-hidden">
                  <div className="relative aspect-video">
                    <img
                      src={getImageUrl(video.image_url)}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs rounded">
                      {video.duration}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-2 line-clamp-2">{video.title}</h3>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{video.views} vues</span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog("videos", video)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem("videos", video.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {videos.length === 0 && (
                <Card className="p-8 text-center col-span-full">
                  <p className="text-muted-foreground">Aucune vidéo</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* GALLERY TAB */}
          <TabsContent value="gallery">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Galerie</h2>
              <Button onClick={() => openDialog("gallery")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {gallery.map((item) => (
                <Card key={item.id} className="overflow-hidden group">
                  <div className="relative aspect-square">
                    <img
                      src={getImageUrl(item.image_url)}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openDialog("gallery", item)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteItem("gallery", item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-bold text-sm">{item.title}</h3>
                    <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                  </CardContent>
                </Card>
              ))}
              {gallery.length === 0 && (
                <Card className="p-8 text-center col-span-full">
                  <p className="text-muted-foreground">Aucune image</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* WORDS TAB */}
          <TabsContent value="words">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                <Book className="w-6 h-6 text-primary" />
                Dictionnaire ({allWords.length} mots)
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={wordsSearch}
                    onChange={(e) => setWordsSearch(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                <Select value={wordsCategory} onValueChange={setWordsCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={() => openDialog("words")}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un mot
                </Button>
              </div>
            </div>
            
            <div className="grid gap-3">
              {allWords
                .filter(word => {
                  const matchesSearch = wordsSearch === "" || 
                    word.baoulé?.toLowerCase().includes(wordsSearch.toLowerCase()) ||
                    word.français?.toLowerCase().includes(wordsSearch.toLowerCase());
                  const matchesCategory = wordsCategory === "all" || word.catégorie === wordsCategory;
                  return matchesSearch && matchesCategory;
                })
                .map((word) => (
                  <Card key={word.word_id} className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading text-xl font-bold text-primary">{word.baoulé}</h3>
                          {word.phonétique && (
                            <span className="text-sm text-muted-foreground">{word.phonétique}</span>
                          )}
                          <span className="px-2 py-0.5 bg-muted rounded text-xs">{word.catégorie}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            word.niveau === "débutant" ? "bg-green-100 text-green-700" :
                            word.niveau === "intermédiaire" ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-700"
                          }`}>{word.niveau}</span>
                        </div>
                        <p className="text-foreground">{word.français}</p>
                        {word.exemple_baoulé && (
                          <p className="text-sm text-muted-foreground mt-1 italic">"{word.exemple_baoulé}"</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const audio = new Audio(`${API}/words/${word.word_id}/audio`);
                            audio.play();
                          }}
                        >
                          <Volume2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDialogType("words");
                            setEditingItem(word);
                            setFormData({
                              baoulé: word.baoulé || "",
                              français: word.français || "",
                              phonétique: word.phonétique || "",
                              catégorie: word.catégorie || "général",
                              exemple_baoulé: word.exemple_baoulé || "",
                              exemple_français: word.exemple_français || "",
                              niveau: word.niveau || "débutant",
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteItem("words", word.word_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              
              {allWords.filter(word => {
                const matchesSearch = wordsSearch === "" || 
                  word.baoulé?.toLowerCase().includes(wordsSearch.toLowerCase()) ||
                  word.français?.toLowerCase().includes(wordsSearch.toLowerCase());
                const matchesCategory = wordsCategory === "all" || word.catégorie === wordsCategory;
                return matchesSearch && matchesCategory;
              }).length === 0 && (
                <Card className="p-8 text-center">
                  <Book className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun mot trouvé</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* PRENOMS TAB */}
          <TabsContent value="prenoms">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Prénoms Baoulé</h2>
                <p className="text-sm text-muted-foreground">
                  Gérez les prénoms traditionnels baoulé
                </p>
              </div>
              <Button onClick={() => openDialog("prenoms")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un prénom
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-4 mb-6">
              <Card className="p-3">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{prenoms.length}</p>
              </Card>
              <Card className="p-3">
                <p className="text-sm text-muted-foreground">👨 Masculins</p>
                <p className="text-2xl font-bold">
                  {prenoms.filter(p => p.genre === "masculin").length}
                </p>
              </Card>
              <Card className="p-3">
                <p className="text-sm text-muted-foreground">👩 Féminins</p>
                <p className="text-2xl font-bold">
                  {prenoms.filter(p => p.genre === "feminin").length}
                </p>
              </Card>
            </div>

            {/* Liste des prénoms */}
            <div className="grid gap-4">
              {prenoms.map((prenom) => (
                <Card key={prenom.prenom_id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">
                            {prenom.genre === "masculin" ? "👨" : "👩"}
                          </span>
                          <div>
                            <h3 className="font-bold text-xl">{prenom.prenom}</h3>
                            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                              {prenom.genre}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          <span className="font-semibold">Signification :</span>{" "}
                          {prenom.signification}
                        </p>
                        {prenom.origine && (
                          <p className="text-xs text-muted-foreground">
                            <span className="font-semibold">Origine :</span>{" "}
                            {prenom.origine}
                          </p>
                        )}
                        {prenom.variantes && prenom.variantes.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="font-semibold">Variantes :</span>{" "}
                            {prenom.variantes.join(", ")}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3 text-red-500" />
                            <span className="text-xs">Popularité: {prenom.popularite || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog("prenoms", prenom)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem("prenoms", prenom.prenom_id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {prenoms.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    Aucun prénom baoulé
                  </p>
                  <Button onClick={() => openDialog("prenoms")}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter le premier prénom
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ARTICLES TAB */}
          <TabsContent value="articles">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Articles</h2>
              <Button onClick={() => openDialog("articles")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <div className="grid gap-4">
              {articles.map((article) => (
                <Card key={article.id} className="overflow-hidden">
                  <div className="flex">
                    {article.image_url && (
                      <div className="w-32 h-24 flex-shrink-0">
                        <img
                          src={getImageUrl(article.image_url)}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="text-xs font-bold text-primary">
                            {article.category}
                          </span>
                          <h3 className="font-bold">{article.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {article.excerpt}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {article.author} • {article.time}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog("articles", article)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteItem("articles", article.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
              {articles.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">Aucun article</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* TRENDS TAB */}
          <TabsContent value="trends">
            <div className="space-y-8">
              {/* Word of the Day Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Mot du Jour
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-2 block">Sélectionner un mot</Label>
                      <Select value={selectedWordId} onValueChange={setSelectedWordId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un mot..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {allWords.map((word) => (
                            <SelectItem key={word.word_id} value={word.word_id}>
                              {word.baoulé} - {word.français}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        className="mt-4 w-full"
                        onClick={async () => {
                          if (!selectedWordId) {
                            toast.error("Veuillez sélectionner un mot");
                            return;
                          }
                          const token = localStorage.getItem("token");
                          const res = await fetch(`${API}/cms/word-of-day`, {
                            method: "PUT",
                            headers: { 
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}` 
                            },
                            body: JSON.stringify({ word_id: selectedWordId })
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setWordOfDay({ date: new Date().toISOString().split('T')[0], word: data.word, manual: true });
                            toast.success("Mot du jour mis à jour!");
                          } else {
                            toast.error("Erreur lors de la mise à jour");
                          }
                        }}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Définir comme Mot du Jour
                      </Button>
                    </div>
                    
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-2">Mot actuel :</p>
                      {wordOfDay?.word ? (
                        <div>
                          <p className="font-heading text-2xl font-bold text-primary">{wordOfDay.word.baoulé}</p>
                          <p className="text-muted-foreground">{wordOfDay.word.phonétique}</p>
                          <p className="text-lg mt-1">{wordOfDay.word.français}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {wordOfDay.manual ? "✋ Défini manuellement" : "🎲 Sélection automatique"}
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Aucun mot défini pour aujourd'hui</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expression of the Week Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                    Expression de la Semaine
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      (Semaine {expressionOfWeek?.week || "?"})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Expression en Baoulé</Label>
                        <Input
                          value={expressionForm.expression}
                          onChange={(e) => setExpressionForm({...expressionForm, expression: e.target.value})}
                          placeholder="Ex: Akwaba, a wuli yé?"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Signification</Label>
                        <Input
                          value={expressionForm.meaning}
                          onChange={(e) => setExpressionForm({...expressionForm, meaning: e.target.value})}
                          placeholder="Ex: Bienvenue, comment vas-tu?"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Traduction littérale</Label>
                        <Input
                          value={expressionForm.literal}
                          onChange={(e) => setExpressionForm({...expressionForm, literal: e.target.value})}
                          placeholder="Ex: Bienvenue, tu t'es réveillé?"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Contexte d'utilisation</Label>
                        <Input
                          value={expressionForm.context}
                          onChange={(e) => setExpressionForm({...expressionForm, context: e.target.value})}
                          placeholder="Ex: Salutation formelle"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <Button 
                      className="w-full md:w-auto"
                      onClick={async () => {
                        if (!expressionForm.expression || !expressionForm.meaning) {
                          toast.error("Expression et signification requises");
                          return;
                        }
                        const token = localStorage.getItem("token");
                        const res = await fetch(`${API}/cms/expression-of-week`, {
                          method: "PUT",
                          headers: { 
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}` 
                          },
                          body: JSON.stringify(expressionForm)
                        });
                        if (res.ok) {
                          toast.success("Expression de la semaine mise à jour!");
                          fetchAllData();
                        } else {
                          toast.error("Erreur lors de la mise à jour");
                        }
                      }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer l'expression
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary/10 rounded-xl">
                      <Target className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{allWords.length}</p>
                      <p className="text-sm text-muted-foreground">Mots dans le dictionnaire</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-xl">
                      <Hash className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{hashtags.length}</p>
                      <p className="text-sm text-muted-foreground">Hashtags actifs</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-100 rounded-xl">
                      <BookPlus className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{contributions.length}</p>
                      <p className="text-sm text-muted-foreground">Contributions en attente</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* COMMUNITY TAB */}
          <TabsContent value="community">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-primary" />
                Fil Communautaire ({communityPosts.length} posts)
              </h2>
              <Button onClick={() => openDialog("posts")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un post
              </Button>
            </div>
            
            <div className="space-y-4">
              {communityPosts.length > 0 ? (
                communityPosts.map((post) => (
                  <Card key={post.id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {post.author_name?.charAt(0) || "?"}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-bold">{post.author_name}</h4>
                              <p className="text-sm text-muted-foreground">{post.time}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setDialogType("posts");
                                  setEditingItem(post);
                                  setFormData({
                                    author_name: post.author_name || "",
                                    author_avatar: post.author_avatar || "",
                                    content: post.content || "",
                                    image_url: post.image_url || "",
                                    likes: post.likes || 0,
                                    comments: post.comments || 0,
                                    time: post.time || "À l'instant",
                                  });
                                  setDialogOpen(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteItem("posts", post.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <p className="mb-3">{post.content}</p>
                          
                          {post.image_url && (
                            <img
                              src={getImageUrl(post.image_url)}
                              alt="Post"
                              className="w-full max-w-md rounded-lg mb-3"
                            />
                          )}
                          
                          <div className="flex items-center gap-6 pt-3 border-t border-border text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" />
                              {post.likes} J'aime
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {post.comments} Commentaires
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun post communautaire</p>
                  <Button onClick={() => openDialog("posts")}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le premier post
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* HASHTAGS TAB */}
          <TabsContent value="hashtags">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Hashtags</h2>
              <Button onClick={() => openDialog("hashtags")}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {hashtags.map((hashtag) => (
                <Card key={hashtag.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg text-primary">{hashtag.tag}</h3>
                      <p className="text-sm text-muted-foreground">
                        {hashtag.posts} posts • {hashtag.trend}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog("hashtags", hashtag)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteItem("hashtags", hashtag.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {hashtags.length === 0 && (
                <Card className="p-8 text-center col-span-full">
                  <p className="text-muted-foreground">Aucun hashtag</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Gestion des Utilisateurs</h2>
              <div className="text-sm text-muted-foreground">
                {allUsers.length} utilisateur(s)
              </div>
            </div>
            <div className="grid gap-4">
              {allUsers.map((u) => (
                <Card key={u.user_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {u.picture ? (
                        <img
                          src={u.picture}
                          alt={u.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="font-bold text-primary">
                            {u.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold flex items-center gap-2">
                          {u.name}
                          {u.role === "admin" && (
                            <span className="px-2 py-0.5 rounded bg-primary text-white text-xs flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Admin
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        <p className="text-xs text-muted-foreground">
                          {u.points || 0} points • {u.streak || 0} jours de streak
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={u.role || "user"}
                        onValueChange={(value) => updateUserRole(u.user_id, value)}
                        disabled={u.user_id === user?.user_id}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Utilisateur</SelectItem>
                          <SelectItem value="admin">Administrateur</SelectItem>
                        </SelectContent>
                      </Select>
                      {u.user_id !== user?.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(u.user_id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {allUsers.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">Aucun utilisateur</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* CONTRIBUTIONS TAB */}
          <TabsContent value="contributions">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Contributions</h2>
              <div className="flex items-center gap-2">
                <Select value={contributionFilter} onValueChange={async (val) => {
                  setContributionFilter(val);
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${API}/contributions?status=${val}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (res.ok) setContributions(await res.json());
                }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuvées</SelectItem>
                    <SelectItem value="rejected">Rejetées</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {contributions.length > 0 ? (
              <div className="space-y-4">
                {contributions.map((contrib) => (
                  <Card key={contrib.contribution_id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-2xl font-bold text-primary">
                              {contrib.baoulé}
                            </h3>
                            {contrib.phonétique && (
                              <span className="text-muted-foreground">{contrib.phonétique}</span>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              contrib.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              contrib.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {contrib.status === 'pending' ? 'En attente' :
                               contrib.status === 'approved' ? 'Approuvée' : 'Rejetée'}
                            </span>
                          </div>
                          
                          <p className="text-lg mb-3">{contrib.français}</p>
                          
                          {contrib.exemple_baoulé && (
                            <div className="p-3 bg-muted/50 rounded-lg mb-3">
                              <p className="text-sm italic">"{contrib.exemple_baoulé}"</p>
                              {contrib.exemple_français && (
                                <p className="text-sm text-muted-foreground mt-1">→ {contrib.exemple_français}</p>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="px-2 py-1 bg-muted rounded">{contrib.catégorie}</span>
                            <span>Par: {contrib.user_id}</span>
                            <span>{new Date(contrib.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                        
                        {contrib.status === 'pending' && (
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={async () => {
                                const token = localStorage.getItem("token");
                                const res = await fetch(`${API}/contributions/${contrib.contribution_id}/approve`, {
                                  method: "PUT",
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success("Contribution approuvée et ajoutée au dictionnaire!");
                                  setContributions(contributions.filter(c => c.contribution_id !== contrib.contribution_id));
                                } else {
                                  toast.error("Erreur lors de l'approbation");
                                }
                              }}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approuver
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                const token = localStorage.getItem("token");
                                const res = await fetch(`${API}/contributions/${contrib.contribution_id}/reject`, {
                                  method: "PUT",
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success("Contribution rejetée");
                                  setContributions(contributions.filter(c => c.contribution_id !== contrib.contribution_id));
                                } else {
                                  toast.error("Erreur lors du rejet");
                                }
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Rejeter
                            </Button>
                          </div>
                        )}
                        
                        {contrib.status !== 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              const token = localStorage.getItem("token");
                              const res = await fetch(`${API}/contributions/${contrib.contribution_id}`, {
                                method: "DELETE",
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                toast.success("Contribution supprimée");
                                setContributions(contributions.filter(c => c.contribution_id !== contrib.contribution_id));
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <BookPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {contributionFilter === 'pending' 
                    ? "Aucune contribution en attente" 
                    : `Aucune contribution ${contributionFilter === 'approved' ? 'approuvée' : 'rejetée'}`}
                </p>
              </Card>
            )}
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Site Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Paramètres Généraux
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du site</Label>
                      <Input
                        value={settings.site_name || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, site_name: e.target.value })
                        }
                        placeholder="Mon Baoulé"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slogan</Label>
                      <Input
                        value={settings.site_tagline || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, site_tagline: e.target.value })
                        }
                        placeholder="Langue & Culture • Côte d'Ivoire"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CTA Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Section Appel à l'Action (CTA)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre CTA</Label>
                    <Input
                      value={settings.cta_title || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, cta_title: e.target.value })
                      }
                      placeholder="Rejoignez la communauté Mon Baoulé"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sous-titre CTA</Label>
                    <Textarea
                      value={settings.cta_subtitle || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, cta_subtitle: e.target.value })
                      }
                      placeholder="Apprenez, pratiquez et partagez..."
                      rows={2}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Texte du bouton</Label>
                      <Input
                        value={settings.cta_button_text || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, cta_button_text: e.target.value })
                        }
                        placeholder="Commencer l'aventure"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lien du bouton</Label>
                      <Input
                        value={settings.cta_button_link || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, cta_button_link: e.target.value })
                        }
                        placeholder="/register"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Buzz Section Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-destructive" />
                    Section "Ce qui Buzz"
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre de la section</Label>
                    <Input
                      value={settings.buzz_title || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, buzz_title: e.target.value })
                      }
                      placeholder="Ce qui Buzz"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Titre du défi</Label>
                      <Input
                        value={settings.buzz_challenge_title || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, buzz_challenge_title: e.target.value })
                        }
                        placeholder="Défi du Jour"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Objectif du défi</Label>
                      <Input
                        type="number"
                        value={settings.buzz_challenge_target || 5}
                        onChange={(e) =>
                          setSettings({ ...settings, buzz_challenge_target: parseInt(e.target.value) })
                        }
                        placeholder="5"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description du défi</Label>
                    <Input
                      value={settings.buzz_challenge_description || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, buzz_challenge_description: e.target.value })
                      }
                      placeholder="Apprenez 5 nouveaux mots et gagnez 50 points"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Section Titles */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-secondary" />
                    Titres des Sections
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Section Articles</Label>
                      <Input
                        value={settings.section_articles_title || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, section_articles_title: e.target.value })
                        }
                        placeholder="Dernières Actualités"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Section Vidéos</Label>
                      <Input
                        value={settings.section_videos_title || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, section_videos_title: e.target.value })
                        }
                        placeholder="📹 Vidéos de la Semaine"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Section Galerie</Label>
                      <Input
                        value={settings.section_gallery_title || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, section_gallery_title: e.target.value })
                        }
                        placeholder="📸 Galerie Culturelle"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Section Communauté</Label>
                      <Input
                        value={settings.section_community_title || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, section_community_title: e.target.value })
                        }
                        placeholder="💬 Fil Communautaire"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footer Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LayoutDashboard className="w-5 h-5 text-accent" />
                    Footer & Réseaux Sociaux
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Description footer</Label>
                    <Textarea
                      value={settings.footer_description || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, footer_description: e.target.value })
                      }
                      placeholder="La plateforme de référence pour..."
                      rows={2}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Facebook URL</Label>
                      <Input
                        value={settings.social_facebook || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, social_facebook: e.target.value })
                        }
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram URL</Label>
                      <Input
                        value={settings.social_instagram || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, social_instagram: e.target.value })
                        }
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Twitter/X URL</Label>
                      <Input
                        value={settings.social_twitter || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, social_twitter: e.target.value })
                        }
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>YouTube URL</Label>
                      <Input
                        value={settings.social_youtube || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, social_youtube: e.target.value })
                        }
                        placeholder="https://youtube.com/..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Popular Links Management */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-500" />
                      Liens Populaires (Sidebar)
                    </span>
                    <Button size="sm" onClick={() => openDialog("popular-links")}>
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {popularLinks.map((link, index) => (
                      <div key={link.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
                          <span className="font-medium">{link.title}</span>
                          <span className="text-sm text-muted-foreground">{link.url}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDialogType("popular-links");
                              setEditingItem(link);
                              setFormData({ title: link.title, url: link.url, order: link.order });
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteItem("popular-links", link.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {popularLinks.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">Aucun lien populaire</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={fetchAllData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button onClick={saveSettings} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Sauvegarde..." : "Sauvegarder tous les paramètres"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Modifier" : "Ajouter"}{" "}
              {dialogType === "heroes" && "un article héros"}
              {dialogType === "videos" && "une vidéo"}
              {dialogType === "gallery" && "une image"}
              {dialogType === "articles" && "un article"}
              {dialogType === "hashtags" && "un hashtag"}
              {dialogType === "words" && "un mot"}
              {dialogType === "prenoms" && "un prénom"}
              {dialogType === "popular-links" && "un lien populaire"}
              {dialogType === "posts" && "un post communautaire"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* HEROES FORM */}
            {dialogType === "heroes" && (
              <>
                <div className="space-y-2">
                  <Label>Image *</Label>
                  <ImageUpload
                    value={formData.image_url || ""}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="La Danse Goli : Quand les Masques Prennent Vie"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Extrait</Label>
                  <Textarea
                    value={formData.excerpt || ""}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Plongée au cœur de cette tradition..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Input
                      value={formData.category || ""}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="REPORTAGE"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Icône catégorie</Label>
                    <Input
                      value={formData.category_icon || ""}
                      onChange={(e) => setFormData({ ...formData, category_icon: e.target.value })}
                      placeholder="📺"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Auteur</Label>
                    <Input
                      value={formData.author || ""}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="Kouassi Amani"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      value={formData.date || ""}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      placeholder="25 Jan 2026"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Temps de lecture</Label>
                    <Input
                      value={formData.read_time || ""}
                      onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                      placeholder="8 min"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_main || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_main: checked })}
                  />
                  <Label>Article principal (grande image)</Label>
                </div>
              </>
            )}

            {/* VIDEOS FORM */}
            {dialogType === "videos" && (
              <>
                <div className="space-y-2">
                  <Label>Miniature *</Label>
                  <ImageUpload
                    value={formData.image_url || ""}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Tutoriel : Les Bases du Baoulé"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL vidéo (YouTube, etc.)</Label>
                  <Input
                    value={formData.video_url || ""}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Durée</Label>
                    <Input
                      value={formData.duration || ""}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="15:32"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vues</Label>
                    <Input
                      value={formData.views || ""}
                      onChange={(e) => setFormData({ ...formData, views: e.target.value })}
                      placeholder="25K"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Likes</Label>
                    <Input
                      value={formData.likes || ""}
                      onChange={(e) => setFormData({ ...formData, likes: e.target.value })}
                      placeholder="1.2K"
                    />
                  </div>
                </div>
              </>
            )}

            {/* GALLERY FORM */}
            {dialogType === "gallery" && (
              <>
                <div className="space-y-2">
                  <Label>Image *</Label>
                  <ImageUpload
                    value={formData.image_url || ""}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Masques Sacrés de Sakassou"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sous-titre</Label>
                  <Input
                    value={formData.subtitle || ""}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Collection privée"
                  />
                </div>
              </>
            )}

            {/* ARTICLES FORM */}
            {dialogType === "articles" && (
              <>
                <div className="space-y-2">
                  <Label>Image</Label>
                  <ImageUpload
                    value={formData.image_url || ""}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="30 Écoles Adoptent le Baoulé"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Extrait</Label>
                  <Textarea
                    value={formData.excerpt || ""}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Une initiative révolutionnaire..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Input
                      value={formData.category || ""}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="📚 ÉDUCATION"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Auteur</Label>
                    <Input
                      value={formData.author || ""}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="N'guessan Adjoua"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Temps (relatif)</Label>
                  <Input
                    value={formData.time || ""}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    placeholder="Il y a 2h"
                  />
                </div>
              </>
            )}

            {/* HASHTAGS FORM */}
            {dialogType === "hashtags" && (
              <>
                <div className="space-y-2">
                  <Label>Hashtag *</Label>
                  <Input
                    value={formData.tag || ""}
                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                    placeholder="#MotDuJour"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de posts</Label>
                    <Input
                      type="number"
                      value={formData.posts || 0}
                      onChange={(e) => setFormData({ ...formData, posts: parseInt(e.target.value) || 0 })}
                      placeholder="450"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tendance</Label>
                    <Select
                      value={formData.trend || "new"}
                      onValueChange={(value) => setFormData({ ...formData, trend: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="up">📈 En hausse</SelectItem>
                        <SelectItem value="down">📉 En baisse</SelectItem>
                        <SelectItem value="stable">➖ Stable</SelectItem>
                        <SelectItem value="new">✨ Nouveau</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* WORDS FORM */}
            {dialogType === "words" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mot en Baoulé *</Label>
                    <Input
                      value={formData.baoulé || ""}
                      onChange={(e) => setFormData({ ...formData, baoulé: e.target.value })}
                      placeholder="Ex: Akwaba"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Traduction en Français *</Label>
                    <Input
                      value={formData.français || ""}
                      onChange={(e) => setFormData({ ...formData, français: e.target.value })}
                      placeholder="Ex: Bienvenue"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phonétique</Label>
                    <Input
                      value={formData.phonétique || ""}
                      onChange={(e) => setFormData({ ...formData, phonétique: e.target.value })}
                      placeholder="Ex: /a-kwa-ba/"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select
                      value={formData.catégorie || "général"}
                      onValueChange={(value) => setFormData({ ...formData, catégorie: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salutations">Salutations</SelectItem>
                        <SelectItem value="nombres">Nombres</SelectItem>
                        <SelectItem value="famille">Famille</SelectItem>
                        <SelectItem value="nourriture">Nourriture</SelectItem>
                        <SelectItem value="nature">Nature</SelectItem>
                        <SelectItem value="verbes">Verbes</SelectItem>
                        <SelectItem value="expressions">Expressions</SelectItem>
                        <SelectItem value="proverbes">Proverbes</SelectItem>
                        <SelectItem value="général">Général</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Niveau</Label>
                  <Select
                    value={formData.niveau || "débutant"}
                    onValueChange={(value) => setFormData({ ...formData, niveau: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="débutant">🟢 Débutant</SelectItem>
                      <SelectItem value="intermédiaire">🟡 Intermédiaire</SelectItem>
                      <SelectItem value="avancé">🔴 Avancé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Exemple en Baoulé</Label>
                  <Input
                    value={formData.exemple_baoulé || ""}
                    onChange={(e) => setFormData({ ...formData, exemple_baoulé: e.target.value })}
                    placeholder="Ex: Akwaba, a wuli yé?"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Traduction de l'exemple</Label>
                  <Input
                    value={formData.exemple_français || ""}
                    onChange={(e) => setFormData({ ...formData, exemple_français: e.target.value })}
                    placeholder="Ex: Bienvenue, comment vas-tu?"
                  />
                </div>
              </>
            )}

            {/* PRENOMS FORM */}
            {dialogType === "prenoms" && (
              <>
                <div className="space-y-2">
                  <Label>
                    Prénom <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={formData.prenom || ""}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    placeholder="Ex: Kouassi, Akissi..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Genre <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.genre || "masculin"}
                    onValueChange={(value) => setFormData({ ...formData, genre: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masculin">👨 Masculin</SelectItem>
                      <SelectItem value="feminin">👩 Féminin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    Signification <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={formData.signification || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, signification: e.target.value })
                    }
                    placeholder="Ex: Né un dimanche, Née un samedi..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Origine</Label>
                  <Input
                    value={formData.origine || ""}
                    onChange={(e) => setFormData({ ...formData, origine: e.target.value })}
                    placeholder="Ex: Jour de naissance, Rang de naissance..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Popularité (1-100)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.popularite || 50}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        popularite: parseInt(e.target.value) || 50,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Plus le chiffre est élevé, plus le prénom sera populaire
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Variantes (séparées par des virgules)</Label>
                  <Input
                    value={
                      Array.isArray(formData.variantes)
                        ? formData.variantes.join(", ")
                        : formData.variantes || ""
                    }
                    onChange={(e) => {
                      const variantes = e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter((v) => v);
                      setFormData({ ...formData, variantes });
                    }}
                    placeholder="Ex: Kwasi, Kouassy, Kwassi"
                  />
                  <p className="text-xs text-muted-foreground">
                    Autres orthographes du prénom
                  </p>
                </div>
              </>
            )}

            {/* POPULAR LINKS FORM */}
            {dialogType === "popular-links" && (
              <>
                <div className="space-y-2">
                  <Label>Titre du lien *</Label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="🎓 Guide Débutant"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL *</Label>
                  <Input
                    value={formData.url || ""}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="/learn"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ordre d'affichage</Label>
                  <Input
                    type="number"
                    value={formData.order || 0}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </>
            )}

            {/* POSTS FORM */}
            {dialogType === "posts" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom de l'auteur *</Label>
                    <Input
                      value={formData.author_name || ""}
                      onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                      placeholder="Kouadio Emmanuel"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date/Heure</Label>
                    <Input
                      value={formData.time || ""}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      placeholder="Il y a 30 minutes"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Contenu du post *</Label>
                  <Textarea
                    value={formData.content || ""}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="🎉 J'ai terminé ma première semaine d'apprentissage..."
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Image (optionnel)</Label>
                  <ImageUpload
                    currentUrl={formData.image_url}
                    onUpload={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de J'aime</Label>
                    <Input
                      type="number"
                      value={formData.likes || 0}
                      onChange={(e) => setFormData({ ...formData, likes: parseInt(e.target.value) || 0 })}
                      placeholder="127"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre de commentaires</Label>
                    <Input
                      type="number"
                      value={formData.comments || 0}
                      onChange={(e) => setFormData({ ...formData, comments: parseInt(e.target.value) || 0 })}
                      placeholder="23"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveItem} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
